// Libs
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';

// Hooks
import useIsInitialized from '../../hooks/useIsInitialized';

// Components
import Portal from '../portal';

import './search-input.scss';

interface Option {
	label: string | number;
	value: any;
}

interface TextInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onSelect'> {
	options?: Array<Option>;
	onSelect?: (value: Option['value']) => void;
}

const SearchInput: React.ForwardRefRenderFunction<HTMLInputElement | null, TextInputProps> = (
	{ options = [], onSelect, onChange, ...props },
	ref
) => {
	const [isListOpen, setIsListOpen] = useState(false);
	const isInitialized = useIsInitialized();
	const optionsContainerRef = useRef<any>();
	const containerRef = useRef<any>();
	const optionsRef = useRef<any>();

	// @ts-ignore
	const bounds: any = useMemo(() => ref.current?.getBoundingClientRect(), [isListOpen]);

	function matchText(text: string, match?: string) {
		if (!match) {
			return text;
		}

		const startIndex = text.search(new RegExp(match, 'ig'));

		if (startIndex !== -1) {
			return (
				<>
					{text.substr(0, startIndex)}
					<span className="match">{text.substr(startIndex, match.length)}</span>
					{text.substr(startIndex + match.length)}
				</>
			);
		} else {
			return text;
		}
	}

	function onGlobalClick(e: MouseEvent) {
		if (optionsContainerRef.current?.isEqualNode(e.target) || !containerRef.current?.contains(e.target)) {
			setIsListOpen(false);
		}
	}

	function onSelectMiddleware(value: Option['value']) {
		if (onSelect) {
			onSelect(value);
		}

		setTimeout(() => {
			setIsListOpen(false);
		}, 0);
	}

	function onSelectByKey(value: Option['value']) {
		return (e: React.KeyboardEvent<HTMLLIElement>) => {
			if (e.keyCode === 13) {
				onSelectMiddleware(value);
			}
		};
	}

	function onChangeMiddleware(e: React.ChangeEvent<HTMLInputElement>) {
		onChange?.(e);

		if (e.target.value.length < 3) {
			setIsListOpen(false);
		}
	}

	function onInputKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
		if (e.keyCode === 40) {
			e.preventDefault();
			optionsRef.current?.children?.[0]?.focus();
		} else if (e.keyCode === 27) {
			// @ts-ignore
			ref?.current?.blur();
		}
	}

	function onOptionsKeyDown(e: React.KeyboardEvent<HTMLUListElement>) {
		const children = optionsRef.current.children;
		// @ts-ignore
		const currentIndex = +e.target.getAttribute('tabIndex');
		const nextElement = children[currentIndex + 1];
		const previousElement = children[currentIndex - 1];

		if (e.keyCode === 40 && nextElement) {
			e.preventDefault();
			nextElement.focus();
		} else if (e.keyCode === 38 && previousElement) {
			e.preventDefault();
			previousElement.focus();
		} else if (e.keyCode === 27) {
			children[currentIndex].blur();
			setIsListOpen(false);
		}
	}

	useEffect(() => {
		if (props.value && options.length && isInitialized) {
			setIsListOpen(true);
		}
	}, [props.value, options]);

	useEffect(() => {
		window.addEventListener('click', onGlobalClick);
		return () => window.removeEventListener('click', onGlobalClick);
	}, []);

	return (
		<div className="search-input-container" ref={containerRef}>
			<input
				className="text-input"
				ref={ref}
				{...props}
				onKeyDown={onInputKeyDown}
				onChange={onChangeMiddleware}
			/>

			{isListOpen && (
				<Portal>
					<div
						className="search-input-container__options-container"
						ref={optionsContainerRef}
						style={{
							pointerEvents: isListOpen ? 'all' : 'none',
							top: bounds.top + 16,
							left: bounds.left - 8,
							width: bounds.width + 16,
							zIndex: 100
						}}
						onFocus={props.onFocus}
						onBlur={props.onBlur}
					>
						<div style={{ overflow: 'hidden' }}>
							<ul
								className="search-input-container__options"
								ref={optionsRef}
								style={{
									animation: isListOpen
										? 'search-options-in 0.25s ease both'
										: 'search-options-out 0.25s ease both'
								}}
								onKeyDown={onOptionsKeyDown}
							>
								{options?.map(({ label, value }, index) => {
									return (
										<li
											key={`${index}-${label}`}
											className="search-input-container__options__item"
											tabIndex={index}
											onKeyDown={onSelectByKey(value)}
											onClick={() => onSelectMiddleware(value)}
										>
											{matchText(label.toString(), props.value?.toString())}
										</li>
									);
								})}
							</ul>
						</div>
					</div>
				</Portal>
			)}
		</div>
	);
};

export default forwardRef(SearchInput);
